'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsSurfaceTypeSectionDisplayed from '@alltrails/amplitude/events/logTrailDetailsSurfaceTypeSectionDisplayed';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Hide from '@alltrails/denali/icons/Hide';
import Showing from '@alltrails/denali/icons/Showing';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import SurfaceTypeBreakdown from '@alltrails/maps/components/SurfaceTypeBreakdown/SurfaceTypeBreakdown';
import { useGetTrailSurfaceTypesQuery } from '@alltrails/maps/redux/mapsApi';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import TrailSurfaceTypesData from '@alltrails/maps/types/TrailSurfaceTypesData';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import updateSurfaceTypeIsHidden from '@/utils/updateSurfaceTypeIsHidden';
import styles from './SurfaceType.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type SurfaceTypeSectionProps = {
  data: TrailSurfaceTypesData;
  isTrailAdmin: boolean;
  trailId: number;
  trailPageType: TrailPageType;
};

// This is split into its own component so that useLogWebSectionViewed isn't called until we know that the section will be shown, and the ref will be set
const SurfaceTypeSection = ({ data, isTrailAdmin, trailId, trailPageType }: SurfaceTypeSectionProps) => {
  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { displayMetric, dynamicStyleIds } = useAppSelector(state => ({
    displayMetric: state.context.displayMetric,
    dynamicStyleIds: state.map.dynamicStyleIds
  }));

  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, 'Surface Type', 'trail page');

  const [isSurfaceTypeHidden, setIsSurfaceTypeHidden] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  useEffect(() => {
    setIsSurfaceTypeHidden(data.hiddenFromUser || false);
  }, [data]);

  const openSurfaceTypeOnMap = () => {
    dispatch(setIsTrailMapOpen(true));
    if (!dynamicStyleIds.includes('surfaceType')) {
      dispatch(updateDynamicStyleIds([...dynamicStyleIds, 'surfaceType']));
    }
  };

  const onShowOnMapClick = () => {
    logTrailDetailsActionTapped({
      trail_id: String(trailId),
      trail_page_action: 'surface type show as map tapped',
      trail_page_location: 'surface type',
      trail_page_type: trailPageType
    });

    if (user) {
      openSurfaceTypeOnMap();
    } else {
      setIsSignupModalOpen(true);
    }
  };

  const getTrailAdminButton = () => {
    if (!isTrailAdmin) {
      return null;
    }

    const getOnClick = (shouldHide: boolean) => () => {
      updateSurfaceTypeIsHidden(trailId, shouldHide)
        .then(({ hiddenFromUser }) => {
          setIsSurfaceTypeHidden(hiddenFromUser);
        })
        .catch(error => {
          console.log(`Failed to update surface type visibility: ${error}`);
        });
    };

    if (isSurfaceTypeHidden) {
      return (
        <Button
          icon={{ Component: Showing }}
          onClick={getOnClick(false)}
          size="sm"
          text={<FormattedMessage defaultMessage="Show this module" />}
          variant="primary"
        />
      );
    }
    return (
      <Button
        icon={{ Component: Hide }}
        onClick={getOnClick(true)}
        size="sm"
        text={<FormattedMessage defaultMessage="Hide this module" />}
        variant="ghost"
        elevated
      />
    );
  };

  return (
    <>
      <div className={styles.card} ref={ref}>
        <div className={styles.container}>
          <Heading as="h2" size="300">
            <FormattedMessage defaultMessage="Surface type" />
          </Heading>
          <SurfaceTypeBreakdown
            className={!isMobile && isTrailAdmin && isSurfaceTypeHidden ? styles.hiddenBreakdown : undefined}
            maxItems={4}
            showVisualBreakdown
            surfaceTypeAggregation={data!.surfaceTypes.aggregation}
            useMetric={displayMetric}
            verticalSpacing="compact"
          />
        </div>
        <div className={styles.buttons}>
          <Button
            elevated={!isMobile}
            onClick={onShowOnMapClick}
            size="md"
            text={<FormattedMessage defaultMessage="Show on map" />}
            variant={isMobile ? 'secondary' : 'ghost'}
            width={isMobile ? 'full' : undefined}
          />
          {getTrailAdminButton()}
        </div>
      </div>
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignupModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          isOpen={isSignupModalOpen}
          onSuccess={() => {
            setIsSignupModalOpen(false);
            openSurfaceTypeOnMap();
          }}
          trigger="Surface Type Show on Map"
          upsellTrigger="Surface Type Show on Map"
        />
      )}
    </>
  );
};

type SurfaceTypeProps = {
  onHide?: () => void;
  trailId: number;
  trailPageType: TrailPageType;
};

const SurfaceType = ({ onHide, trailId, trailPageType }: SurfaceTypeProps) => {
  const { hasPermission } = useAuthorization();
  const isTrailAdmin = hasPermission({ permission: 'trails:manage' });

  const { data, isError, isLoading } = useGetTrailSurfaceTypesQuery({
    includeSegments: false,
    showHidden: isTrailAdmin,
    trailId
  });

  const shouldHideSurfaceType = useMemo(() => {
    if (isError || !data || !data.surfaceTypes) {
      return true;
    }

    // Trail admins can always see the surface type module
    if (isTrailAdmin) {
      return false;
    }

    if (data.hiddenFromUser) {
      return true;
    }

    const surfaceTypeAggregation = data.surfaceTypes.aggregation;
    if ((surfaceTypeAggregation.find(({ surfaceType }) => surfaceType.identifier === 'unknown')?.percentOfSurface || 0) > 50) {
      // Don't show the surface type module when the unknown surface type is more than 50% of the trail
      return true;
    }
    return false;
  }, [isError, data, isTrailAdmin]);

  useEffect(() => {
    // Wait for the data to be fetched in order to determine if the surface type section will be shown
    if (data && data.surfaceTypes && !isError) {
      logTrailDetailsSurfaceTypeSectionDisplayed({ is_surface_type_shown: !shouldHideSurfaceType, trail_id: String(trailId) });
    }
  }, [isError, data, shouldHideSurfaceType, trailId]);

  useEffect(() => {
    if (!isLoading && shouldHideSurfaceType) {
      onHide?.();
    }
  }, [isLoading, shouldHideSurfaceType, onHide]);

  if (isLoading) {
    return <SurfaceTypeLoading />;
  }

  if (shouldHideSurfaceType) {
    // Most trails don't have enough known surface type to display this module,
    // so we don't show an error state since the likely result would be that even if
    // a subsequent request succeeds, the module would still be hidden.
    return null;
  }

  return <SurfaceTypeSection data={data!} isTrailAdmin={isTrailAdmin} trailId={trailId} trailPageType={trailPageType} />;
};

export default SurfaceType;

const SurfaceTypeLoading = () => (
  <ShimmerContainer className={styles.loadingContainer}>
    <div className={styles.card}>
      <div className={styles.container}>
        <div className={classNames(styles.loadingHeading, styles.loading)}>&nbsp;</div>
        <div className={classNames(styles.loadingBar, styles.loading)}>&nbsp;</div>
        <div className={styles.loadingRows}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className={classNames(styles.loadingRow, styles.loading)}>
              &nbsp;
            </div>
          ))}
        </div>
      </div>
      <div className={classNames(styles.loadingButton, styles.loading)}>&nbsp;</div>
    </div>
  </ShimmerContainer>
);
